import Styles from './styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Timestamp, arrayUnion, collection, doc, getDoc, getDocs, getFirestore, limit, onSnapshot,
  orderBy, query, setDoc, where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FaSearch } from 'react-icons/fa';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { useAppSelector } from 'src/salon/app/hook';
import MyCardTab from '../15.evaluation/detail/MyCardTab';
import ResumeTab from '../15.evaluation/detail/ResumeTab';
import {
  RecruitPeriod, VisitInfo, examinationLabel, parseRecruitPeriods, periodIndexFor, resolvePeriods,
} from '../15.evaluation/types';
import { useJudgeIdentity } from '../15.evaluation/useJudgeIdentity';
import DefaultImg from '../../../image/default/account.png';

type AppChat = {
  id: string;
  user: { uid: string; name: string };
  company: { id: string; recruitId: string };
  chat: { chatStartedAt: Date; lastSentAt: Date };
  meta: { chatType: string; memo: string; createdAt: Date };
};

type JudgeRow = {
  id: string;
  kind: 'application' | 'visit';
  recruitId: string;
  uid: string;
  name: string;
  schoolName: string;
  examination: number; // 応募: 1〜5=選考中,6=採用,7=不採用 / 見学: 常に0(単一ラウンドの専用値)
  memo: string;
  appliedAt: Date;
  // 募集回の振り分けに使う応募日(chats.meta.createdAt)
  periodDate: Date;
  // 学生情報に出す卒業予定(例: 2027年3月卒業予定)。未登録なら ''
  graduationLabel: string;
  visit: VisitInfo | null;
  // ログイン中の審査員本人が「評価を提出する」(非ドラフト)を実行済みか。
  // 応募: applicants/{id}.examList{examination} に judgeId が入っているか。
  // 見学: chats/{id}/visitExamination/0_{judgeId} が存在し draft===false か。
  examSubmitted: boolean;
};

type SelectionConfig = { selectionCount: number; texts: string[] };

const SCORE_OPTIONS = [
  { value: 4, mark: '◎', label: 'とても良い' },
  { value: 3, mark: '○', label: '良い' },
  { value: 2, mark: '△', label: '判断できない' },
  { value: 1, mark: '×', label: '気になる' },
  { value: 0, mark: '-', label: '対象外' },
];

const OVERALL_OPTIONS = [
  { value: 4, mark: '◎', label: '強く次へ進めたい', sub: 'ぜひ次の面談へ進めたい' },
  { value: 3, mark: '○', label: '次へ進めたい', sub: '前向きに次へ進めたい' },
  { value: 2, mark: '△', label: '保留', sub: '追加確認が必要' },
  { value: 1, mark: '×', label: '見送り', sub: '今回は見送りたい' },
];

// サロン見学には「次の選考へ進める」という概念がないため、応募の総合判断
// (OVERALL_OPTIONS)とは別に「入社してほしいか」という観点の選択肢にする。
const VISIT_OVERALL_OPTIONS = [
  { value: 4, mark: '◎', label: '強く入社してほしい', sub: 'ぜひ入社してほしい' },
  { value: 3, mark: '○', label: '入社してほしい', sub: '前向きに入社してほしい' },
  { value: 2, mark: '△', label: '保留', sub: '追加確認が必要' },
  { value: 1, mark: '×', label: '見送り', sub: '今回は見送りたい' },
];

function toDate(value: any): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value) return new Date(value);
  return new Date();
}

// 卒業予定(学生アプリのプロフィール profile.myCard.graduation、古いデータは profile.graduation)。
// 未登録なら ''。以前は全員「2027年卒（卒業予定）」と固定で出していた。
function graduationLabelOf(user: Record<string, any> | undefined): string {
  const g = user?.profile?.myCard?.graduation ?? user?.profile?.graduation;
  const year = typeof g?.year === 'number' ? g.year : Number(g?.year);
  if (!year || isNaN(year)) return '';
  const month = typeof g?.month === 'number' ? g.month : Number(g?.month);
  return `${year}年${month && !isNaN(month) ? `${month}月` : ''}卒業予定`;
}

function toChat(id: string, data: Record<string, any>): AppChat {
  return {
    id,
    user: { uid: data.user?.uid ?? '', name: data.user?.name ?? '' },
    company: { id: data.company?.id ?? '', recruitId: data.company?.recruitId ?? '' },
    chat: {
      chatStartedAt: toDate(data.chat?.chatStartedAt),
      lastSentAt: toDate(data.chat?.lastSentAt),
    },
    meta: {
      chatType: data.meta?.chatType ?? '',
      memo: data.meta?.memo ?? '',
      // 応募日(募集回の振り分けに使う)。無い古いチャットはチャットの開始日
      createdAt: toDate(data.meta?.createdAt ?? data.chat?.chatStartedAt),
    },
  };
}

async function fetchLatestVisit(chatId: string): Promise<VisitInfo | null> {
  const db = getFirestore();
  const snap = await getDocs(query(collection(db, 'chats', chatId, 'visits'), orderBy('requestedAt', 'desc'), limit(1)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data();
  return {
    id: d.id,
    status: data.status ?? '',
    preferredDates: Array.isArray(data.preferredDates) ? data.preferredDates.map((t: any) => toDate(t)) : [],
    interestedTopics: Array.isArray(data.interestedTopics) ? data.interestedTopics : [],
    preferredStoreName: data.preferredStoreName ?? '',
  };
}

// PCでは「学生情報｜審査」の2ペイン(splitShell)を維持し、審査員が使うことが
// 多いスマホ幅の時だけ、My Card/履歴書/希望・見学情報と同列の「審査」タブに
// まとめた1カラム構成に切り替える。
function useIsNarrow(breakpoint: number): boolean {
  const [ isNarrow, setIsNarrow ] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handleChange = () => setIsNarrow(mql.matches);
    handleChange();
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, [breakpoint]);

  return isNarrow;
}

const Judging: React.FC = () => {
  const company = useAppSelector((state) => state.company.company);
  const judge = useJudgeIdentity();
  const isMobile = useIsNarrow(900);

  const [ rows, setRows ] = useState<JudgeRow[]>([]);
  const [ selectedId, setSelectedId ] = useState('');
  const [ loading, setLoading ] = useState(true);
  const [ loadError, setLoadError ] = useState(false);
  // 「応募の審査」と「サロン見学の審査」は評価の観点(選考段階の有無・総合判断の
  // 選択肢・保存先コレクション)が別物なので、1つの一覧に混ぜず、上部の切替で
  // どちらか一方だけを対象にする。
  const [ judgeMode, setJudgeMode ] = useState<'application' | 'visit'>('application');
  // null=従来の1人ずつ審査。'examined'/'visitDone'=審査済み一覧タブ(応募/サロン見学)を表示。

  // 応募の審査の選考段階での絞り込み('all' = すべて)
  const [ stageFilter, setStageFilter ] = useState<'all' | number>('all');
  // 募集回を使っている会社では「応募の審査」を「第1回応募の審査」「第2回応募の審査」…に分ける。選んでいる回。
  const [ roundFilter, setRoundFilter ] = useState<number | null>(null);
  // 審査前/審査済みの切り替え。審査済み = ログイン中の審査員が、学生の今の段階(応募)・
  // 見学について評価を提出済み。応募は段階が進むと、次の段階の「審査前」に移る。
  const [ reviewFilter, setReviewFilter ] = useState<'pending' | 'done'>('pending');
  // 「審査前」で評価を提出した直後の学生。そのままだと一覧から外れて画面が切り替わってしまうため、
  // 別の学生に移るまでは表示に残す。
  const [ stickyId, setStickyId ] = useState<string>('');
  const [ materialTab, setMaterialTab ] = useState<'mycard' | 'resume' | 'judge'>('mycard');
  const [ config, setConfig ] = useState<SelectionConfig>({ selectionCount: 0, texts: [] });
  const [ scores, setScores ] = useState<number[]>(new Array(8).fill(0));
  const [ comments, setComments ] = useState<string[]>(new Array(8).fill(''));
  const [ overallScore, setOverallScore ] = useState(0);
  const [ overallComment, setOverallComment ] = useState('');
  const [ strengthComment, setStrengthComment ] = useState('');
  const [ concernComment, setConcernComment ] = useState('');
  const [ saving, setSaving ] = useState(false);
  // 保存直後のフィードバックは別要素(span)で出すとactionBarが折り返して
  // 提出ボタンの位置がずれるため、押されたボタン自身の色/文言を変えて表示する。
  const [ savedAction, setSavedAction ] = useState<'draft' | 'submit' | null>(null);
  const [ error, setError ] = useState('');
  const [ nameInput, setNameInput ] = useState('');
  const [ autoNext, setAutoNext ] = useState(false);
  const [ searchText, setSearchText ] = useState('');
  const [ recruitPeriods, setRecruitPeriods ] = useState<RecruitPeriod[]>(() => parseRecruitPeriods(undefined));
  const periods = useMemo(() => resolvePeriods(recruitPeriods), [recruitPeriods]);

  // 募集回(締めた回の学生は審査の対象から外す)。求人のドキュメントは誰でも読める。
  useEffect(() => {
    if (!company?.id) return;
    return onSnapshot(
      doc(getFirestore(), 'recruits', `${company.id}_2027`),
      (snap) => setRecruitPeriods(parseRecruitPeriods(snap.data()?.recruitPeriods)),
      (error) => console.error('募集回を読み込めませんでした', error),
    );
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    setLoadError(false);
    const db = getFirestore();
    const chatsQuery = query(
      collection(db, 'chats'),
      where('company.id', '==', company.id),
      orderBy('chat.lastSentAt', 'desc'),
    );

    const unsubscribe = onSnapshot(chatsQuery, async (snap) => {
      const chats = snap.docs.map((d) => toChat(d.id, d.data()));
      const applicationChats = chats.filter((c) => c.meta.chatType === 'application');
      const visitChats = chats.filter((c) => c.meta.chatType === 'visit');

      // 既に応募チャットへ紐づけ済み(希望・見学情報タブに表示)の見学チャットは、
      // 下の見学単独行としては重複させない。
      const attachedVisitChatIds = new Set<string>();

      const applicationRows = await Promise.all(applicationChats.map(async (chat) => {
        const [ applicantSnap, userSnap ] = await Promise.all([
          getDoc(doc(db, 'applicants', chat.id)),
          chat.user.uid ? getDoc(doc(db, 'users', chat.user.uid)) : Promise.resolve(null),
        ]);
        const applicant = applicantSnap.exists() ? applicantSnap.data() : {};
        const relatedVisitChat = visitChats.find((v) => v.user.uid === chat.user.uid);
        if (relatedVisitChat) attachedVisitChatIds.add(relatedVisitChat.id);
        const visit = relatedVisitChat ? await fetchLatestVisit(relatedVisitChat.id).catch(() => null) : null;
        const stage = typeof applicant.examination === 'number' ? applicant.examination : 1;
        const examList = applicant[`examList${stage}`];
        return {
          id: chat.id,
          kind: 'application' as const,
          recruitId: chat.company.recruitId || applicant.recruitId || '',
          uid: chat.user.uid,
          name: (applicant.basicInfo?.name || chat.user.name || '').trim(),
          schoolName: userSnap?.exists() ? userSnap.data()?.education?.school ?? '' : '',
          examination: stage,
          memo: chat.meta.memo,
          appliedAt: chat.meta.createdAt,
          periodDate: chat.meta.createdAt,
          graduationLabel: userSnap?.exists() ? graduationLabelOf(userSnap.data()) : '',
          visit,
          examSubmitted: !!judge.judgeId && Array.isArray(examList) && examList.includes(judge.judgeId),
        };
      }));

      // まだ応募していない「サロン見学のみ」の学生も審査対象に含める。選考段階(1〜5次)は
      // 存在しないため、examinationは見学専用の固定値0を使う(単一ラウンドのみ、
      // 選考の1〜7とは重ならない)。
      const standaloneVisitChats = visitChats.filter((v) => !attachedVisitChatIds.has(v.id));
      const visitRows = await Promise.all(standaloneVisitChats.map(async (chat) => {
        const [ userSnap, visit, visitExamSnap ] = await Promise.all([
          chat.user.uid ? getDoc(doc(db, 'users', chat.user.uid)) : Promise.resolve(null),
          fetchLatestVisit(chat.id).catch(() => null),
          judge.judgeId
            ? getDoc(doc(db, 'chats', chat.id, 'visitExamination', `0_${judge.judgeId}`)).catch(() => null)
            : Promise.resolve(null),
        ]);
        return {
          id: chat.id,
          kind: 'visit' as const,
          recruitId: chat.company.recruitId || '',
          uid: chat.user.uid,
          name: (chat.user.name || '').trim(),
          schoolName: userSnap?.exists() ? userSnap.data()?.education?.school ?? '' : '',
          examination: 0,
          memo: chat.meta.memo,
          appliedAt: chat.meta.createdAt,
          periodDate: chat.meta.createdAt,
          graduationLabel: userSnap?.exists() ? graduationLabelOf(userSnap.data()) : '',
          visit,
          examSubmitted: !!visitExamSnap?.exists() && visitExamSnap.data()?.draft === false,
          hasOwnEvaluation: !!visitExamSnap?.exists(),
        };
      }));

      // 見学の審査は、見学が終わってから行うもの。見学済み(completed)の学生だけを審査の対象に出す
      // (日程調整中・日程確定・キャンセルの学生は出さない)。ただし、この審査員がすでに評価を
      // 書いている学生は、状態を戻した場合などに書いた評価が見えなくならないよう残す。
      const visitRowsToJudge = visitRows.filter((r) => r.visit?.status === 'completed' || r.hasOwnEvaluation);

      const nextRows = [...applicationRows, ...visitRowsToJudge];
      setRows(nextRows);
      setSelectedId((current) => (current && nextRows.some((r) => r.id === current) ? current : nextRows[0]?.id ?? ''));
      setLoading(false);
    }, (err) => {
      // 審査員セッションでchatsが読めない(Firestore Rulesのchats参加者判定が
      // owner限定でjudgeを弾いている等)と、ハンドラ無しでは
      // "Uncaught Error in snapshot listener"だけ出てloadingが解けず
      // 画面が「読み込み中」で固まる。エラー表示に切り替える。
      console.error('judging chats fetch error:', err);
      setLoading(false);
      setLoadError(true);
    });

    return () => unsubscribe();
  }, [company?.id, judge.judgeId]);

  // 応募者評価(15.evaluation)と同じフリーワード検索。氏名・学校名を対象に絞り込み、
  // 「前の学生/次の学生」の送りも絞り込み後の一覧を対象に動く。
  //
  // 応募は、締めた募集回(応募設定の「募集回」)の学生を出さない。締めた回の記録は確定しているため審査せず、
  // 審査員には過去の履歴を見せない。サロン見学は募集回と関係なく出す。
  const searchedRows = useMemo(() => {
    const text = searchText.trim();
    const activeRows = rows.filter((r) => r.kind === 'visit' || periods[periodIndexFor(periods, r.periodDate)]?.state !== 'closed');
    if (!text) return activeRows;
    return activeRows.filter((r) => (r.name + r.schoolName).includes(text));
  }, [rows, searchText, periods]);

  // 応募の審査の対象は、選考中(1次〜5次選考)の学生だけ。採用(6)・不採用(7)が決まった学生は
  // もう審査しないため、対象にも選考段階の切り替えにも出さない。
  const isUnderSelection = (r: { kind: string; examination: number }) =>
    r.kind === 'application' && r.examination >= 1 && r.examination <= 5;

  // 上部のタブの件数は、まだ評価していない(審査前の)学生の数
  const applicationCount = useMemo(() => searchedRows.filter((r) => isUnderSelection(r) && !r.examSubmitted).length, [searchedRows]);
  const visitCount = useMemo(() => searchedRows.filter((r) => r.kind === 'visit' && !r.examSubmitted).length, [searchedRows]);

  // 実際に一覧・ページャ・「前/次の学生」が対象にするのは、選択中モード(応募 or
  // サロン見学)に一致する行だけ。
  // 応募の審査では、選考段階(1次・2次…)でも絞り込める。
  //
  // 募集回を使っている会社(回が2つ以上ある)では、「応募の審査」を締めていない回ごとのタブ
  // (第1回応募の審査・第2回応募の審査…)に分け、選んだ回の学生だけを審査する。学生情報にも「第n回応募」を出す。
  const roundOf = (r: JudgeRow) => periodIndexFor(periods, r.periodDate);
  const multiPeriod = periods.filter((p) => p.state !== 'upcoming').length > 1;
  const allApplicationRows = useMemo(() => searchedRows.filter(isUnderSelection), [searchedRows]);
  const roundOptions = useMemo(() => {
    if (!multiPeriod) return [];
    return periods
      .filter((p) => p.state !== 'closed')
      .map((p) => {
        const rowsInRound = allApplicationRows.filter((r) => roundOf(r) === p.index);
        return { round: p.index, state: p.state, total: rowsInRound.length, pending: rowsInRound.filter((r) => !r.examSubmitted).length };
      })
      // 受付前の回は、応募がある場合だけ出す
      .filter((o) => o.state !== 'upcoming' || o.total > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApplicationRows, periods, multiPeriod]);
  const activeRound: number | 'all' = roundOptions.length === 0
    ? 'all'
    : (roundFilter !== null && roundOptions.some((o) => o.round === roundFilter) ? roundFilter : roundOptions[0].round);
  const applicationRowsInMode = useMemo(
    () => activeRound === 'all' ? allApplicationRows : allApplicationRows.filter((r) => roundOf(r) === activeRound),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allApplicationRows, activeRound, periods],
  );
  const stageOptions = useMemo(() => {
    const counts = new Map<number, number>();
    applicationRowsInMode.forEach((r) => counts.set(r.examination, (counts.get(r.examination) ?? 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => a[0] - b[0]).map(([stage, count]) => ({ stage, count }));
  }, [applicationRowsInMode]);

  // 選んでいた段階の学生がいなくなったら「すべて」に戻す
  useEffect(() => {
    if (stageFilter !== 'all' && !stageOptions.some((o) => o.stage === stageFilter)) setStageFilter('all');
  }, [stageOptions, stageFilter]);

  // 選んでいるモード(応募は選考段階も)に当てはまる学生。ここを審査前/審査済みに分ける。
  const modeRows = useMemo(
    () => judgeMode === 'application'
      ? applicationRowsInMode.filter((r) => stageFilter === 'all' || r.examination === stageFilter)
      : searchedRows.filter((r) => r.kind === judgeMode),
    [searchedRows, applicationRowsInMode, judgeMode, stageFilter],
  );
  const pendingCount = modeRows.filter((r) => !r.examSubmitted).length;
  const doneCount = modeRows.length - pendingCount;

  const visibleRows = useMemo(
    () => modeRows.filter((r) => r.examSubmitted === (reviewFilter === 'done') || r.id === stickyId),
    [modeRows, reviewFilter, stickyId],
  );

  // 別の学生に移ったら、提出直後に残していた学生を外す
  useEffect(() => {
    if (stickyId && selectedId !== stickyId) setStickyId('');
  }, [selectedId, stickyId]);

  const selectedIndex = useMemo(() => visibleRows.findIndex((r) => r.id === selectedId), [visibleRows, selectedId]);
  const selected = selectedIndex >= 0 ? visibleRows[selectedIndex] : null;

  useEffect(() => {
    if (visibleRows.length === 0) return;
    if (!visibleRows.some((r) => r.id === selectedId)) {
      setSelectedId(visibleRows[0].id);
    }
  }, [visibleRows, selectedId]);

  // PC幅に戻った時、審査タブ(モバイル専用)が選択されたままだと学生情報側の
  // タブ内容(My Card/履歴書/希望・見学情報)が何も表示されなくなるため戻す。
  useEffect(() => {
    if (!isMobile && materialTab === 'judge') {
      setMaterialTab('mycard');
    }
  }, [isMobile, materialTab]);

  // 見学の学生には履歴書が無いため、履歴書を開いたまま見学の学生に移ったら My Card に戻す
  useEffect(() => {
    if (selected?.kind === 'visit' && materialTab === 'resume') setMaterialTab('mycard');
  }, [selected?.kind, materialTab]);

  useEffect(() => {
    if (!selected) return;
    setMaterialTab('mycard');
    setError('');
    setSavedAction(null);
    setScores(new Array(8).fill(0));
    setComments(new Array(8).fill(''));
    setOverallScore(0);
    setOverallComment('');
    setStrengthComment('');
    setConcernComment('');

    (async () => {
      const db = getFirestore();
      const recruitSnap = selected.recruitId ? await getDoc(doc(db, 'recruits', selected.recruitId)) : null;
      const recruitData = recruitSnap?.exists() ? recruitSnap.data() : {};
      const stage = selected.examination;
      setConfig(selected.kind === 'visit' ? {
        selectionCount: recruitData?.visitExamCount ?? 0,
        texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => recruitData?.[`visitExamText${i}`] ?? ''),
      } : {
        selectionCount: recruitData?.[`exam_selection${stage}Count`] ?? 0,
        texts: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => recruitData?.[`exam_selection${stage}Text${i}`] ?? ''),
      });

      const examCollectionRef = selected.kind === 'visit'
        ? collection(db, 'chats', selected.id, 'visitExamination')
        : collection(db, 'applicants', selected.id, 'examination');
      const examSnap = await getDocs(query(examCollectionRef, where('evaluation', '==', stage)));
      const records = examSnap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          uid: data.uid ?? '',
          name: data.name ?? '',
          scores: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => Number(data[`evaluation${i}Score`] ?? 0)),
          comments: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => String(data[`evaluation${i}Comment`] ?? '')),
          overallScore: Number(data.overallScore ?? 0),
          overallComment: String(data.overallComment ?? ''),
          strengthComment: String(data.strengthComment ?? ''),
          concernComment: String(data.concernComment ?? ''),
          draft: !!data.draft,
        };
      });

      const own = records.find((r) => r.uid === judge.judgeId);
      if (own) {
        setScores(own.scores);
        setComments(own.comments);
        setOverallScore(own.overallScore);
        setOverallComment(own.overallComment);
        setStrengthComment(own.strengthComment);
        setConcernComment(own.concernComment);
      }
    })();
  }, [selected, judge.judgeId]);

  function moveStudent(delta: number) {
    if (selectedIndex < 0) return;
    const next = visibleRows[selectedIndex + delta];
    if (next) setSelectedId(next.id);
  }

  function setScore(index: number, value: number) {
    setScores((prev) => prev.map((s, i) => i === index ? value : s));
  }

  function setComment(index: number, value: string) {
    setComments((prev) => prev.map((c, i) => i === index ? value : c));
  }

  async function save(isDraft: boolean) {
    if (!selected || !judge.isSet) return;
    setError('');
    if (!isDraft) {
      for (let i = 0; i < config.selectionCount; i++) {
        if (scores[i] === 0) {
          setError(`「${config.texts[i] || `審査${i + 1}`}」を評価してください。`);
          return;
        }
      }
      if (overallScore === 0) {
        setError('今回の総合判断を選択してください。');
        return;
      }
      if (!overallComment.trim()) {
        setError('評価コメントを入力してください。');
        return;
      }
    }

    setSaving(true);
    try {
      const db = getFirestore();
      const docId = `${selected.examination}_${judge.judgeId}`;
      const data: Record<string, unknown> = {
        uid: judge.judgeId,
        authUid: getAuth().currentUser?.uid ?? '',
        name: judge.name,
        evaluation: selected.examination,
        overallScore,
        overallComment,
        strengthComment,
        concernComment,
        draft: isDraft,
        submittedAt: isDraft ? null : Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      for (let i = 0; i < 8; i++) {
        data[`evaluation${i + 1}Score`] = scores[i];
        data[`evaluation${i + 1}Comment`] = comments[i];
      }

      // サロン見学のみ(未応募)の学生には対応するapplicantsドキュメントが存在しないため、
      // 親chatドキュメント配下のvisitExaminationに保存する(firestore.rules参照)。
      const examDocRef = selected.kind === 'visit'
        ? doc(db, 'chats', selected.id, 'visitExamination', docId)
        : doc(db, 'applicants', selected.id, 'examination', docId);
      await setDoc(examDocRef, data, { merge: true });
      if (!isDraft && selected.examination >= 1 && selected.examination <= 5) {
        await setDoc(doc(db, 'applicants', selected.id), {
          [`examList${selected.examination}`]: arrayUnion(judge.judgeId),
        }, { merge: true });
      }

      // 一覧の「審査済み」は chats の変更時にしか数え直されないため(評価の提出では chats は
      // 変わらない)、提出したらその学生をこの画面上でも審査済みにする。
      // 「審査前」で提出すると、その学生は「審査済み」に移る。
      //   - 次の学生へ自動で移動する: 一覧で次にいた学生を開く
      //   - しない: 提出した学生を、別の学生に移るまで表示に残す(すぐ画面が切り替わらないように)
      const nextId = !isDraft && reviewFilter === 'pending'
        ? (visibleRows[selectedIndex + 1]?.id ?? visibleRows[selectedIndex - 1]?.id ?? '')
        : '';
      if (!isDraft) {
        const submittedId = selected.id;
        if (reviewFilter === 'pending' && !autoNext) setStickyId(submittedId);
        setRows((prev) => prev.map((r) => (r.id === submittedId ? { ...r, examSubmitted: true } : r)));
      }

      setSavedAction(isDraft ? 'draft' : 'submit');
      setTimeout(() => setSavedAction(null), 1600);

      if (!isDraft && autoNext) {
        if (reviewFilter === 'pending') {
          if (nextId) setTimeout(() => setSelectedId(nextId), 500);
        } else {
          setTimeout(() => moveStudent(1), 500);
        }
      }
    } catch (e: any) {
      setError(e?.message ?? '保存に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return <div className={Styles.page}><p className={Styles.empty}>応募者情報の取得に失敗しました。権限設定を確認のうえ、時間をおいて再度お試しください。</p></div>;
  }

  if (loading) {
    return <div className={Styles.page}><p className={Styles.empty}>読み込み中...</p></div>;
  }

  // 審査本体(選考フロー表示〜評価入力〜提出)。PCでは常に右側の.judgePaneに表示し、
  // スマホでは「審査」タブの中身として同じものを表示する。
  // selected が無い(このモードに対象者がいない)場合は下部でempty表示に切り替えるため
  // ここでは組み立てない。
  const judgeSection = !selected ? null : (
    <>
      <section className={Styles.flowSection}>
        <h3>{selected.kind === 'visit' ? 'サロン見学の審査' : `選考フロー：${examinationLabel(selected.examination)}`}</h3>
        <div className={Styles.criteriaNotice}>
          <p>
            {selected.kind === 'visit'
              ? 'サロン見学では、以下の項目を中心に評価してください（選考段階のような複数ラウンドはありません）。'
              : `今回（${examinationLabel(selected.examination)}）では、以下の項目を中心に評価してください。`}
          </p>
          <div>
            {config.texts.slice(0, config.selectionCount).map((text, i) => (
              <span key={i}>{text || `審査${i + 1}`}</span>
            ))}
          </div>
        </div>
      </section>

      {!judge.isSet ? (
        <section className={Styles.judgeNameBox}>
          <h3>評価者名を入力してください</h3>
          <input value={nameInput} onChange={(e) => setNameInput(e.target.value)} placeholder="評価者名" />
          <button type="button" onClick={() => judge.setName(nameInput)}>この名前で審査を始める</button>
        </section>
      ) : (
        <>
          {config.selectionCount > 0 && (
            <section className={Styles.scoreSection}>
              <div className={Styles.sectionTitleRow}>
                <h3>評価項目</h3>
                <span>各項目をタップして評価してください</span>
              </div>
              <div className={Styles.scoreTable}>
                {config.texts.slice(0, config.selectionCount).map((text, i) => (
                  <div className={Styles.scoreRow} key={i}>
                    <div className={Styles.scoreName}>
                      <h4>{text || `審査${i + 1}`}</h4>
                      <p>{comments[i] || '必要に応じて項目ごとのメモを残せます'}</p>
                    </div>
                    <div className={Styles.scoreButtonsRow}>
                      {SCORE_OPTIONS.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          className={scores[i] === option.value ? Styles.scoreActive : Styles.scoreButton}
                          onClick={() => setScore(i, option.value)}
                        >
                          <span>{option.mark}</span>
                          <small>{option.label}</small>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={comments[i]}
                      onChange={(e) => setComment(i, e.target.value)}
                      maxLength={120}
                      placeholder="メモ"
                    />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className={Styles.overallSection}>
            <div className={Styles.sectionTitleRow}>
              <h3>総合判断</h3>
              <span>{selected.kind === 'visit' ? '今回のサロン見学を踏まえた総合的な評価を選択してください' : '今回の面接に対する総合的な評価を選択してください'}</span>
            </div>
            <div className={Styles.overallGrid}>
              {(selected.kind === 'visit' ? VISIT_OVERALL_OPTIONS : OVERALL_OPTIONS).map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={overallScore === option.value ? Styles.overallActive : Styles.overallButton}
                  onClick={() => setOverallScore(option.value)}
                >
                  <strong>{option.mark} {option.label}</strong>
                  <span>{option.sub}</span>
                </button>
              ))}
            </div>
            <label className={Styles.fullComment}>
              評価コメント <span>（必須）</span>
              <textarea
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                maxLength={500}
                placeholder="良かった点・気になった点を入力してください"
              />
              <small>{overallComment.length} / 500</small>
            </label>
          </section>
        </>
      )}

      {error && <p className={Styles.error}>{error}</p>}

      <footer className={Styles.actionBar}>
        <label>
          <input type="checkbox" checked={autoNext} onChange={(e) => setAutoNext(e.target.checked)} />
          次の学生へ自動で移動する
        </label>
        <span>評価者：{judge.name || '未設定'}</span>
        <button
          type="button"
          className={savedAction === 'draft' ? Styles.draftButtonSaved : Styles.draftButton}
          onClick={() => save(true)}
          disabled={saving || !judge.isSet}
        >
          {savedAction === 'draft' ? '下書きを保存しました。' : '下書き保存'}
        </button>
        <button
          type="button"
          className={savedAction === 'submit' ? Styles.submitButtonSaved : Styles.submitButton}
          onClick={() => save(false)}
          disabled={saving || !judge.isSet}
        >
          {saving ? '保存中...' : savedAction === 'submit' ? '評価を提出しました。' : '評価を提出する'}
        </button>
      </footer>
    </>
  );

  // 審査で見る資料は My Card と履歴書だけ(履歴書は応募の学生のみ)。以前あった「希望・見学情報」
  // (応募日・見学の希望日時など)は日程調整のための情報で評価には使わないため外し、
  // サロンのメモだけ学生情報の下に出す。
  const materialTabsList = [
    { key: 'mycard' as const, label: 'My Card' },
    ...(selected?.kind === 'application' ? [{ key: 'resume' as const, label: '履歴書' }] : []),
    // 「審査」タブはスマホ幅の時だけ表示する(PCでは常に右側の.judgePaneに表示する)
    ...(isMobile ? [{ key: 'judge' as const, label: '審査' }] : []),
  ];

  const studentHero = !selected ? null : (
    <div className={Styles.studentHero}>
      <img
        className={Styles.avatar}
        src={selected.kind === 'application'
          ? `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/resume%2F${selected.id}?alt=media`
          : `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/profiles%2Fresize_images%2F${selected.uid}_500x500?alt=media`}
        alt=""
        onError={(e) => {
          // 写真が無い(読み込めない)ときは「画像なし」の画像を出す(空白だと何も分からないため)
          e.currentTarget.onerror = null;
          e.currentTarget.src = DefaultImg;
        }}
      />
      <div>
        <h4>
          {selected.name || '名前未設定'}
          {multiPeriod && selected.kind === 'application' && (
            <span className={Styles.roundBadge}>第{roundOf(selected) + 1}回応募</span>
          )}
        </h4>
        <p>{selected.schoolName || '学校名未設定'}</p>
        {selected.graduationLabel && <p>{selected.graduationLabel}</p>}
      </div>
    </div>
  );

  // サロンが残したメモ(chats.meta.memo)。審査員に共有したい内容が入ることがあるため、入っている学生だけ出す。
  const salonMemo = !selected?.memo ? null : (
    <div className={Styles.salonMemo}>
      <span className={Styles.salonMemoLabel}>サロンのメモ</span>
      <p>{selected.memo}</p>
    </div>
  );

  const materialTabsBar = materialTabsList.length <= 1 ? null : (
    <div className={Styles.materialTabs}>
      {materialTabsList.map((tab) => (
        <button
          type="button"
          key={tab.key}
          className={materialTab === tab.key ? Styles.materialTabActive : Styles.materialTab}
          onClick={() => setMaterialTab(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const infoTabsBody = !selected ? null : (
    <>
      {/* MyCardTab は詳細パネルの余白(20px)いっぱいに背景を広げるため、外側に -20px の余白を持つ。
          そのままだとタブの帯にくっつくので、ここで上に余白を足す */}
      {materialTab === 'mycard' && <div className={Styles.myCardHolder}><MyCardTab uid={selected.uid} /></div>}
      {materialTab === 'resume' && <ResumeTab applicationId={selected.id} />}
      {/* スマホの「審査」タブの中身。PCではこの位置には出さず.judgePaneに常時表示する */}
      {isMobile && materialTab === 'judge' && judgeSection}
    </>
  );

  const isModeActive = (mode: 'application' | 'visit') => judgeMode === mode;

  const modeTabs = (
    <div className={Styles.modeTabs} role="tablist">
      {roundOptions.length === 0 ? (
        <button
          type="button"
          role="tab"
          aria-selected={isModeActive('application')}
          className={isModeActive('application') ? Styles.modeTabActive : Styles.modeTab}
          onClick={() => { setJudgeMode('application'); setStickyId(''); }}
        >
          応募の審査<span>{applicationCount}</span>
        </button>
      ) : roundOptions.map(({ round, pending }) => {
        const active = isModeActive('application') && activeRound === round;
        return (
          <button
            type="button"
            role="tab"
            key={round}
            aria-selected={active}
            className={active ? Styles.modeTabActive : Styles.modeTab}
            onClick={() => { setJudgeMode('application'); setRoundFilter(round); setStageFilter('all'); setStickyId(''); }}
          >
            第{round + 1}回応募の審査<span>{pending}</span>
          </button>
        );
      })}
      <button
        type="button"
        role="tab"
        aria-selected={isModeActive('visit')}
        className={isModeActive('visit') ? Styles.modeTabActive : Styles.modeTab}
        onClick={() => { setJudgeMode('visit'); setStickyId(''); }}
      >
        サロン見学の審査<span>{visitCount}</span>
      </button>
    </div>
  );

  // 「応募の審査」を選んでいるときだけ出す、選考段階の切り替え(My Card/履歴書の切り替えと同じ角丸の帯)
  const stageTabs = judgeMode === 'application' && stageOptions.length > 0 ? (
    <div className={Styles.stageTabs} role="tablist" aria-label="選考段階で絞り込む">
      <button
        type="button"
        role="tab"
        aria-selected={stageFilter === 'all'}
        className={stageFilter === 'all' ? Styles.stageTabActive : Styles.stageTab}
        onClick={() => { setStageFilter('all'); setStickyId(''); }}
      >
        すべて<span>{applicationRowsInMode.length}</span>
      </button>
      {stageOptions.map(({ stage, count }) => (
        <button
          type="button"
          role="tab"
          key={stage}
          aria-selected={stageFilter === stage}
          className={stageFilter === stage ? Styles.stageTabActive : Styles.stageTab}
          onClick={() => { setStageFilter(stage); setStickyId(''); }}
        >
          {examinationLabel(stage)}<span>{count}</span>
        </button>
      ))}
    </div>
  ) : null;

  // 審査前/審査済みの切り替え(段階の切り替えの下。応募・見学の両方)
  const reviewTabs = (
    <div className={Styles.stageTabs} role="tablist" aria-label="審査前・審査済みで絞り込む">
      <button
        type="button"
        role="tab"
        aria-selected={reviewFilter === 'pending'}
        className={reviewFilter === 'pending' ? Styles.stageTabActive : Styles.stageTab}
        onClick={() => { setReviewFilter('pending'); setStickyId(''); }}
      >
        審査前<span>{pendingCount}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={reviewFilter === 'done'}
        className={reviewFilter === 'done' ? Styles.stageTabActive : Styles.stageTab}
        onClick={() => { setReviewFilter('done'); setStickyId(''); }}
      >
        審査済み<span>{doneCount}</span>
      </button>
    </div>
  );

  return (
    <div className={Styles.page}>
      <header className={Styles.topBar}>
        <div className={Styles.topSearchBox}>
          <FaSearch className={Styles.topSearchIcon} />
          <input
            placeholder="フリーワード検索"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className={Styles.studentPager}>
          <button type="button" onClick={() => moveStudent(-1)} disabled={selectedIndex <= 0}><IoChevronBack /> 前の学生</button>
          <span>{visibleRows.length === 0 ? '0 / 0' : `${selectedIndex + 1} / ${visibleRows.length}`}</span>
          <button type="button" onClick={() => moveStudent(1)} disabled={selectedIndex >= visibleRows.length - 1}>次の学生 <IoChevronForward /></button>
        </div>
      </header>

      {modeTabs}
      {stageTabs}
      {reviewTabs}

      {!selected ? (
        <p className={Styles.empty}>
          {reviewFilter === 'pending'
            ? (judgeMode === 'visit' ? '審査前のサロン見学の学生はいません。' : '審査前の学生はいません。')
            : (judgeMode === 'visit' ? '審査済みのサロン見学の学生はいません。' : '審査済みの学生はいません。')}
        </p>
      ) : isMobile ? (
        <div className={Styles.panel}>
          <h3>学生情報</h3>
          {studentHero}
          {salonMemo}
          {materialTabsBar}
          <div className={Styles.materialBody}>
            {infoTabsBody}
          </div>
        </div>
      ) : (
        <div className={Styles.splitShell}>
          <aside className={Styles.infoPane}>
            <h3>学生情報</h3>
            {studentHero}
            {salonMemo}
            {materialTabsBar}
            <div className={Styles.materialBody}>
              {infoTabsBody}
            </div>
          </aside>

          <main className={Styles.judgePane}>
            {judgeSection}
          </main>
        </div>
      )}
    </div>
  );
};

export default Judging;
